import { useEffect, useState } from 'react';
import { isEmpty } from 'remeda';
import type { Id } from 'types';

type UseRowSelectionOptions<T extends { id: Id }> = {
  items: T[];
  onChangeSelection?: (selectedItems: T[]) => void;
  onChangeAcrossPages?: (areAllAcrossPagesSelected: boolean) => void;
};

/**
 * Owns the row-selection state for a CustomTable: per-row toggling, select-all
 * on the current page, and the "all rows across every page" flag. Reports
 * changes to the caller from the action that causes them, so callers always
 * receive the latest callback's result.
 */
export const useRowSelection = <T extends { id: Id }>({
  items,
  onChangeSelection,
  onChangeAcrossPages,
}: UseRowSelectionOptions<T>) => {
  const [selectedIds, setSelectedIds] = useState<Id[]>([]);
  const [areAllItemsAcrossPagesSelected, setAreAllItemsAcrossPagesSelected] = useState(false);

  const selectedItems = items.filter(item => selectedIds.includes(item.id));

  // Array.every() returns true on empty arrays
  const areAllItemsOnThisPageSelected =
    !isEmpty(items) && items.every(item => selectedIds.includes(item.id));

  // Report the initial empty selection once on mount.
  // biome-ignore lint/correctness/useExhaustiveDependencies: mount-only notification
  useEffect(() => {
    onChangeSelection?.([]);
    onChangeAcrossPages?.(false);
  }, []);

  // Clear (and report) the selection whenever the page's items change.
  // biome-ignore lint/correctness/useExhaustiveDependencies: keyed on items; the callback is read fresh each render
  useEffect(() => {
    if (isEmpty(selectedIds)) return;
    setSelectedIds([]);
    onChangeSelection?.([]);
  }, [items]);

  const selectRows = (ids: Id[]) => {
    setSelectedIds(ids);
    onChangeSelection?.(items.filter(item => ids.includes(item.id)));
  };

  const setAcrossPages = (areAllAcrossPagesSelected: boolean) => {
    if (areAllAcrossPagesSelected === areAllItemsAcrossPagesSelected) return;
    setAreAllItemsAcrossPagesSelected(areAllAcrossPagesSelected);
    onChangeAcrossPages?.(areAllAcrossPagesSelected);
  };

  const toggleRow = (itemId: Id) =>
    selectRows(
      selectedIds.includes(itemId)
        ? selectedIds.filter(id => id !== itemId)
        : [...selectedIds, itemId],
    );

  const toggleSelectAllOnPage = () => {
    setAcrossPages(false);
    selectRows(areAllItemsOnThisPageSelected ? [] : items.map(item => item.id));
  };

  const toggleAcrossPages = () => setAcrossPages(!areAllItemsAcrossPagesSelected);

  return {
    selectedIds,
    selectedItems,
    areAllItemsOnThisPageSelected,
    areAllItemsAcrossPagesSelected,
    toggleAcrossPages,
    toggleRow,
    toggleSelectAllOnPage,
  };
};
